<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950">
    <!-- Loading State -->
    <div
      v-if="pageLoading"
      class="flex items-center justify-center min-h-screen"
    >
      <div class="loading-spinner w-12 h-12"></div>
    </div>

    <!-- Main Dashboard Content -->
    <div
      v-else
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-gray-900 dark:text-gray-100"
    >
      <!-- Header Section with breadcrumb and actions -->
      <div class="mb-6">
        <div
          class="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <nav
              class="text-sm text-gray-500 dark:text-gray-400"
              aria-label="Breadcrumb"
            >
              <ol class="flex items-center gap-2">
                <li>
                  <router-link class="hover:underline" :to="{ name: 'home' }"
                    >Home</router-link
                  >
                </li>
                <li>/</li>
                <li class="text-gray-700 dark:text-gray-200">Dashboard</li>
              </ol>
            </nav>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              Dashboard
            </h1>
          </div>
          <div class="flex items-center gap-2">
            <button class="btn btn-outline" @click="refreshDashboard">
              Refresh
            </button>
            <button class="btn btn-primary" @click="quickAddMember">
              Quick Add
            </button>
          </div>
        </div>
      </div>

      <!-- Member Search Section -->
      <div class="card mb-8 dark:bg-gray-900">
        <div class="card-header dark:border-gray-800">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Member Search
          </h2>
        </div>
        <div class="card-body dark:text-gray-100">
          <form
            @submit.prevent="scanMember"
            class="flex flex-col sm:flex-row gap-4"
          >
            <div class="flex-1">
              <label class="form-label">Member ID</label>
              <div class="relative">
                <input
                  v-model="memberId"
                  type="text"
                  required
                  class="form-input pr-10 dark:bg-gray-800 dark:border-gray-700 dark:placeholder-gray-500 dark:text-gray-100"
                  placeholder="Enter member ID to scan"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div class="flex items-end">
              <button
                type="submit"
                :disabled="scanLoading"
                class="btn btn-primary"
              >
                <svg
                  v-if="scanLoading"
                  class="animate-spin -ml-1 mr-2 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <svg
                  v-else
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {{ scanLoading ? "Scanning..." : "Scan Member" }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <!-- Today's Revenue -->
        <div
          class="card hover:shadow-medium transition-shadow cursor-pointer"
          @click="showTransaction = true"
        >
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-600">Today's Revenue</p>
                <p class="text-2xl font-bold text-gray-900">
                  ETB {{ dashboardData.revenue?.total?.toFixed(2) || "0.00" }}
                </p>
                <div class="mt-2 space-y-1 text-sm text-gray-500">
                  <div class="flex justify-between">
                    <span>Renewals:</span>
                    <span class="font-medium"
                      >ETB
                      {{
                        dashboardData.revenue?.membership?.toFixed(2) || "0.00"
                      }}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span>New Members:</span>
                    <span class="font-medium"
                      >ETB
                      {{
                        dashboardData.revenue?.newMembers?.toFixed(2) || "0.00"
                      }}</span
                    >
                  </div>
                  <div class="flex justify-between">
                    <span>Others:</span>
                    <span class="font-medium"
                      >ETB
                      {{
                        dashboardData.revenue?.other?.toFixed(2) || "0.00"
                      }}</span
                    >
                  </div>
                </div>
              </div>
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Next Payments -->
        <div
          class="card hover:shadow-medium transition-shadow cursor-pointer"
          @click="nextPayment = true"
        >
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-600">Next Payments</p>
                <p class="text-2xl font-bold text-gray-900">
                  {{ dashboardData.nextPayments || 0 }}
                </p>
                <p class="text-sm text-gray-500 mt-1">
                  Members due for renewal
                </p>
              </div>
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Active Members -->
        <div
          class="card hover:shadow-medium transition-shadow cursor-pointer"
          @click="activeMembers = true"
        >
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-600">Active Members</p>
                <p class="text-2xl font-bold text-gray-900">
                  {{ dashboardData.activeMembers || 0 }}
                </p>
                <p class="text-sm text-gray-500 mt-1">Currently active</p>
              </div>
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Total Members -->
        <div
          class="card hover:shadow-medium transition-shadow cursor-pointer"
          @click="totalMembers = true"
        >
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-600">Total Members</p>
                <p class="text-2xl font-bold text-gray-900">
                  {{ dashboardData.totalMembers || 0 }}
                </p>
                <p class="text-sm text-gray-500 mt-1">All time registrations</p>
              </div>
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-6 h-6 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scan Member Dialog -->
      <div
        v-if="scanLoading"
        class="modal-overlay"
        @click="scanLoading = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="text-lg font-semibold">Scanning Member</h3>
          </div>
          <div class="modal-body">
            <p class="text-sm text-gray-600 dark:text-gray-300">
              Member ID: <span class="font-medium">{{ memberId }}</span>
            </p>
            <div class="mt-4 flex items-center gap-2">
              <div class="loading-spinner w-6 h-6"></div>
              <span class="text-sm">Please wait...</span>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline" @click="scanLoading = false">
              Close
            </button>
          </div>
        </div>
      </div>

      <!-- Quick Add Member Modal -->
      <div v-if="quickAddOpen" class="modal-overlay" @click="quickAddClose">
        <div class="modal-content max-h-[90vh] flex flex-col" @click.stop>
          <div class="modal-header flex-shrink-0">
            <h3 class="text-lg font-semibold">Quick Add Member</h3>
          </div>
          <div class="modal-body flex-1 overflow-y-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="form-label">Full Name</label>
                <input
                  v-model="quickForm.name"
                  type="text"
                  class="form-input"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label class="form-label">Member ID</label>
                <input
                  v-model="quickForm.memberId"
                  type="text"
                  class="form-input"
                  placeholder="M001"
                />
              </div>
              <div>
                <label class="form-label">Phone</label>
                <input
                  v-model="quickForm.phone"
                  type="tel"
                  class="form-input"
                  placeholder="+123456789"
                />
              </div>
              <div>
                <label class="form-label">Email</label>
                <input
                  v-model="quickForm.email"
                  type="email"
                  class="form-input"
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <label class="form-label">Date of Birth</label>
                <input
                  v-model="quickForm.dateOfBirth"
                  type="date"
                  class="form-input"
                />
              </div>
              <div>
                <label class="form-label">Gender</label>
                <select v-model="quickForm.gender" class="form-input">
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label class="form-label">Plan</label>
                <select v-model="quickForm.plan" class="form-input">
                  <option value="Basic">Basic</option>
                  <option value="Premium">Premium</option>
                  <option value="VIP">VIP</option>
                  <option value="Student">Student</option>
                </select>
              </div>
              <div>
                <label class="form-label">Monthly Price (ETB)</label>
                <input
                  v-model.number="quickForm.price"
                  type="number"
                  class="form-input"
                  placeholder="500"
                />
              </div>
              <div>
                <label class="form-label">Join Date</label>
                <input
                  v-model="quickForm.joinDate"
                  type="date"
                  class="form-input"
                />
              </div>
              <div>
                <label class="form-label">Next Payment</label>
                <input
                  v-model="quickForm.nextPayment"
                  type="date"
                  class="form-input"
                />
              </div>
              <div class="md:col-span-2">
                <label class="form-label">Emergency Contact</label>
                <input
                  v-model="quickForm.emergencyContact"
                  type="text"
                  class="form-input"
                  placeholder="Emergency contact name and phone"
                />
              </div>
              <div class="md:col-span-2">
                <label class="form-label">Medical Notes</label>
                <textarea
                  v-model="quickForm.medicalNotes"
                  class="form-input"
                  rows="3"
                  placeholder="Any medical conditions or notes..."
                ></textarea>
              </div>
              <div class="md:col-span-2 flex items-center gap-2">
                <input
                  id="quick-active"
                  type="checkbox"
                  v-model="quickForm.active"
                  class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label for="quick-active" class="text-sm">Active Member</label>
              </div>
            </div>
          </div>
          <div class="modal-footer flex-shrink-0">
            <button class="btn btn-outline" @click="quickAddClose">
              Cancel
            </button>
            <button class="btn btn-primary" @click="quickAddSave">Save</button>
          </div>
        </div>
      </div>

      <!-- Scan Result Dialog -->
      <div
        v-if="scanResultOpen"
        class="modal-overlay"
        @click="scanResultOpen = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold">Member Scan Result</h3>
              <button
                class="btn btn-outline btn-sm"
                @click="scanResultOpen = false"
              >
                Close
              </button>
            </div>
          </div>
          <div class="modal-body">
            <div v-if="scanResult" class="space-y-3">
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Member ID:</span>
                <span class="font-medium">{{ scanResult.memberId }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Name:</span>
                <span class="font-medium">{{ scanResult.name }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Phone:</span>
                <span class="font-medium">{{ scanResult.phone }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Plan:</span>
                <span class="font-medium">{{ scanResult.plan }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Next Payment:</span>
                <span class="font-medium">{{
                  scanResult.nextPayment || "N/A"
                }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Status:</span>
                <span
                  :class="scanResult.statusBadge"
                  class="px-2 py-0.5 rounded text-xs font-medium"
                  >{{ scanResult.statusText }}</span
                >
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Entry Today:</span>
                <span class="font-medium text-green-700">{{
                  scanResult.notEnteredToday
                    ? "Not entered before today"
                    : "Already checked in today"
                }}</span>
              </div>
            </div>
            <div v-else class="text-sm text-red-600">Member not found.</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="scanResultOpen = false">
              Done
            </button>
          </div>
        </div>
      </div>

      <!-- Detail Modals (replacing v-dialog) -->
      <div
        v-if="showTransaction"
        class="modal-overlay"
        @click="showTransaction = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold">Today's Revenue Breakdown</h3>
              <button
                class="btn btn-outline btn-sm"
                @click="showTransaction = false"
              >
                Close
              </button>
            </div>
          </div>
          <div class="modal-body">
            <div class="overflow-x-auto">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th class="table-header-cell">Category</th>
                    <th class="table-header-cell">Amount (ETB)</th>
                  </tr>
                </thead>
                <tbody class="table-body">
                  <tr class="table-row">
                    <td class="table-cell">Renewals</td>
                    <td class="table-cell font-medium">
                      {{
                        dashboardData.revenue?.membership?.toFixed(2) || "0.00"
                      }}
                    </td>
                  </tr>
                  <tr class="table-row">
                    <td class="table-cell">New Members</td>
                    <td class="table-cell font-medium">
                      {{
                        dashboardData.revenue?.newMembers?.toFixed(2) || "0.00"
                      }}
                    </td>
                  </tr>
                  <tr class="table-row">
                    <td class="table-cell">Others</td>
                    <td class="table-cell font-medium">
                      {{ dashboardData.revenue?.other?.toFixed(2) || "0.00" }}
                    </td>
                  </tr>
                  <tr class="table-row">
                    <td class="table-cell font-semibold">Total</td>
                    <td class="table-cell font-bold">
                      {{ dashboardData.revenue?.total?.toFixed(2) || "0.00" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="showTransaction = false">
              Done
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="nextPayment"
        class="modal-overlay"
        @click="nextPayment = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold">Next Payments</h3>
              <button
                class="btn btn-outline btn-sm"
                @click="nextPayment = false"
              >
                Close
              </button>
            </div>
          </div>
          <div class="modal-body">
            <div class="mb-3 flex items-center gap-2">
              <input
                v-model="nextPaymentsSearch"
                type="text"
                class="form-input"
                placeholder="Search member..."
              />
            </div>
            <div class="overflow-x-auto">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th class="table-header-cell">Member</th>
                    <th class="table-header-cell">Due Date</th>
                    <th class="table-header-cell">Amount (ETB)</th>
                  </tr>
                </thead>
                <tbody class="table-body">
                  <tr
                    class="table-row"
                    v-for="row in filteredNextPayments"
                    :key="row.memberId"
                  >
                    <td class="table-cell">{{ row.member }}</td>
                    <td class="table-cell">{{ row.due }}</td>
                    <td class="table-cell font-medium">{{ row.amount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="nextPayment = false">
              Done
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="activeMembers"
        class="modal-overlay"
        @click="activeMembers = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold">Active Members</h3>
              <button
                class="btn btn-outline btn-sm"
                @click="activeMembers = false"
              >
                Close
              </button>
            </div>
          </div>
          <div class="modal-body">
            <div class="overflow-x-auto">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th class="table-header-cell">Member</th>
                    <th class="table-header-cell">Status</th>
                  </tr>
                </thead>
                <tbody class="table-body">
                  <tr
                    class="table-row"
                    v-for="row in activeMembersRows"
                    :key="row.memberId"
                  >
                    <td class="table-cell">{{ row.name }}</td>
                    <td class="table-cell">
                      <span class="badge badge-success">Active</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="activeMembers = false">
              Done
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="totalMembers"
        class="modal-overlay"
        @click="totalMembers = false"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold">Total Members</h3>
              <button
                class="btn btn-outline btn-sm"
                @click="totalMembers = false"
              >
                Close
              </button>
            </div>
          </div>
          <div class="modal-body">
            <div class="overflow-x-auto">
              <table class="table">
                <thead class="table-header">
                  <tr>
                    <th class="table-header-cell">Member</th>
                    <th class="table-header-cell">Joined</th>
                    <th class="table-header-cell">Status</th>
                  </tr>
                </thead>
                <tbody class="table-body">
                  <tr
                    class="table-row"
                    v-for="row in totalMembersRows"
                    :key="row.memberId"
                  >
                    <td class="table-cell">{{ row.name }}</td>
                    <td class="table-cell">{{ row.joinDate }}</td>
                    <td class="table-cell">
                      <span
                        :class="
                          row.active
                            ? 'badge badge-success'
                            : 'badge badge-info'
                        "
                        >{{ row.active ? "Active" : "Inactive" }}</span
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="totalMembers = false">
              Done
            </button>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="card dark:bg-gray-900">
        <div class="card-header dark:border-gray-800">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Recent Activity
          </h2>
        </div>
        <div class="card-body dark:text-gray-100">
          <div class="overflow-x-auto">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th class="table-header-cell">Member</th>
                  <th class="table-header-cell">Activity</th>
                  <th class="table-header-cell">Amount</th>
                  <th class="table-header-cell">Date</th>
                  <th class="table-header-cell">Status</th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  v-for="activity in recentActivities"
                  :key="activity.id"
                  class="table-row"
                >
                  <td class="table-cell">
                    <div class="flex items-center">
                      <div
                        class="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3"
                      >
                        <span class="text-sm font-medium text-gray-600">{{
                          activity.member.charAt(0)
                        }}</span>
                      </div>
                      <span class="font-medium text-gray-900">{{
                        activity.member
                      }}</span>
                    </div>
                  </td>
                  <td class="table-cell">{{ activity.activity }}</td>
                  <td class="table-cell font-medium">
                    ETB {{ activity.amount }}
                  </td>
                  <td class="table-cell text-gray-500">{{ activity.date }}</td>
                  <td class="table-cell">
                    <span :class="getStatusBadgeClass(activity.status)">
                      {{ activity.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: () => ({
    pageLoading: false,
    scanLoading: false,
    memberId: "",
    scanResultOpen: false,
    scanResult: null,
    quickAddOpen: false,
    quickForm: {
      id: null,
      name: "",
      memberId: "",
      phone: "",
      email: "",
      dateOfBirth: "",
      gender: "Male",
      plan: "Basic",
      price: 500,
      joinDate: new Date().toISOString().split("T")[0],
      nextPayment: "",
      emergencyContact: "",
      medicalNotes: "",
      active: true,
    },
    showTransaction: false,
    nextPayment: false,
    activeMembers: false,
    totalMembers: false,
    nextPaymentsSearch: "",
    dashboardData: {
      revenue: { total: 0, membership: 0, newMembers: 0, other: 0 },
      nextPayments: 0,
      activeMembers: 0,
      totalMembers: 0,
      todayCheckins: 0,
      activeNow: 0,
    },
    recentActivities: [],
    nextPaymentsRows: [],
    activeMembersRows: [],
    totalMembersRows: [],
  }),
  computed: {
    filteredNextPayments() {
      const q = this.nextPaymentsSearch.trim().toLowerCase();
      if (!q) return this.nextPaymentsRows;
      return this.nextPaymentsRows.filter((r) =>
        r.member.toLowerCase().includes(q),
      );
    },
  },

  methods: {
    async loadDashboardData() {
      try {
        const [
          summaryRes,
          activitiesRes,
          nextPaymentsRes,
          activeMembersRes,
          totalMembersRes,
        ] = await Promise.all([
          fetch("http://localhost:4000/api/dashboard/summary"),
          fetch("http://localhost:4000/api/dashboard/activities"),
          fetch("http://localhost:4000/api/dashboard/next-payments"),
          fetch("http://localhost:4000/api/dashboard/active-members"),
          fetch("http://localhost:4000/api/dashboard/total-members"),
        ]);

        if (summaryRes.ok) {
          this.dashboardData = await summaryRes.json();
        }
        if (activitiesRes.ok) {
          this.recentActivities = await activitiesRes.json();
        }
        if (nextPaymentsRes.ok) {
          this.nextPaymentsRows = await nextPaymentsRes.json();
        }
        if (activeMembersRes.ok) {
          this.activeMembersRows = await activeMembersRes.json();
        }
        if (totalMembersRes.ok) {
          this.totalMembersRows = await totalMembersRes.json();
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        this.$bus.$emit("toast:show", {
          title: "Failed to load dashboard data",
          type: "error",
        });
      }
    },

    async refreshDashboard() {
      this.$bus.$emit("toast:show", { title: "Refreshing...", type: "info" });
      await this.loadDashboardData();
      this.$bus.$emit("toast:show", {
        title: "Dashboard refreshed",
        type: "success",
      });
    },

    quickAddMember() {
      this.quickForm = {
        id: null,
        name: "",
        memberId: this.generateQuickMemberId(),
        phone: "",
        email: "",
        dateOfBirth: "",
        gender: "Male",
        plan: "Basic",
        price: 500,
        joinDate: new Date().toISOString().split("T")[0],
        nextPayment: "",
        emergencyContact: "",
        medicalNotes: "",
        active: true,
      };
      this.quickAddOpen = true;
    },

    async quickAddClose() {
      this.quickAddOpen = false;
    },

    async quickAddSave() {
      try {
        const response = await fetch("http://localhost:4000/api/members", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.quickForm),
        });

        if (response.ok) {
          this.$bus.$emit("toast:show", {
            title: "Member added successfully",
            type: "success",
          });
          this.quickAddOpen = false;
          await this.loadDashboardData(); // Refresh dashboard
        } else {
          const error = await response.json();
          this.$bus.$emit("toast:show", {
            title: error.message || "Failed to add member",
            type: "error",
          });
        }
      } catch (error) {
        console.error("Error adding member:", error);
        this.$bus.$emit("toast:show", {
          title: "Failed to add member",
          type: "error",
        });
      }
    },

    generateQuickMemberId() {
      const random = Math.floor(Math.random() * 999) + 1;
      return `M${String(random).padStart(3, "0")}`;
    },

    async scanMember() {
      if (!this.memberId) {
        alert("Please enter a member ID");
        return;
      }

      this.scanLoading = true;
      try {
        const response = await fetch(
          `http://localhost:4000/api/members/scan/${this.memberId}`,
        );
        if (response.ok) {
          this.scanResult = await response.json();
        } else {
          this.scanResult = null;
          this.$bus.$emit("toast:show", {
            title: "Member not found",
            type: "error",
          });
        }
        this.scanResultOpen = true;
      } catch (error) {
        console.error("Error scanning member:", error);
        this.$bus.$emit("toast:show", {
          title: "Failed to scan member",
          type: "error",
        });
      } finally {
        this.scanLoading = false;
      }
    },

    getStatusBadgeClass(status) {
      switch (status.toLowerCase()) {
        case "completed":
          return "badge badge-success";
        case "pending":
          return "badge badge-warning";
        case "failed":
          return "badge badge-danger";
        default:
          return "badge badge-info";
      }
    },
  },

  async created() {
    this.pageLoading = true;
    try {
      await this.loadDashboardData();
    } catch (error) {
      console.error("Error initializing dashboard:", error);
    } finally {
      this.pageLoading = false;
    }
  },
};
</script>

<style scoped>
/* Custom styles for dashboard */
.card {
  @apply bg-white rounded-lg shadow-soft border border-gray-200 transition-all duration-200;
}

.card:hover {
  @apply shadow-medium;
}
</style>
